"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AvailabilityClient = void 0;
const _ = require("lodash");
const lru_cache_1 = require("lru-cache");
const Availability_1 = require("./Availability");
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const commons_utils_1 = require("@deliverr/commons-utils");
const business_types_1 = require("@deliverr/business-types");
const DAY_MS = 24 * 60 * 60 * 1000;
const AVAILABILITY_CLIENT_BATCH_LIMIT = (0, commons_utils_1.getEnvNumber)("AVAILABILITY_CLIENT_BATCH_LIMIT", 100);
class AvailabilityClient extends base_client_1.ApiBasicAuthClient {
    constructor(fromZipGroundToZipsCache = new lru_cache_1.LRUCache({
        max: 200,
        ttl: DAY_MS,
    }), fromZipGroundToZipsCacheForPrediction = new lru_cache_1.LRUCache({
        max: 200,
        ttl: DAY_MS,
    }), serviceUrl = process.env.AVAILABILITY_SERVICE_URL, basicAuthUser = process.env.AVAILABILITY_BASIC_AUTH_USER, basicAuthPassword = process.env.AVAILABILITY_BASIC_AUTH_PASSWORD) {
        super({
            baseURL: serviceUrl,
            basicAuthUser,
            basicAuthPassword,
        });
        this.fromZipGroundToZipsCache = fromZipGroundToZipsCache;
        this.fromZipGroundToZipsCacheForPrediction = fromZipGroundToZipsCacheForPrediction;
        if (!serviceUrl) {
            commons_logs_1.log.warn("AVAILABILITY_SERVICE_URL environment variable is not set");
        }
        this.fromZipGroundToZipsCache = fromZipGroundToZipsCache;
        this.fromZipGroundToZipsCacheForPrediction = fromZipGroundToZipsCacheForPrediction;
        if (!basicAuthUser || !basicAuthPassword) {
            commons_logs_1.log.warn("AVAILABILITY_BASIC_AUTH_USER and AVAILABILITY_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async getGroundToZipsByFromZips(fromZip3s, days = 2, estimatedShipDate, otdLevel = business_types_1.OtdLevels.High, verbose = false) {
        const cacheKey = (zip) => [zip, days, estimatedShipDate].filter(Boolean).join("-");
        const cached = _.fromPairs(fromZip3s
            .map((fromZip) => [fromZip, this.fromZipGroundToZipsCache.get(cacheKey(fromZip))])
            .filter((pair) => pair[1]));
        const fromZips = _.difference(fromZip3s, _.keys(cached));
        if (_.isEmpty(fromZips)) {
            if (verbose) {
                commons_logs_1.log.info({ fromZips: _.keys(cached), days, estimatedShipDate }, "Retrieved groundToZips from cache");
            }
            return cached;
        }
        else {
            const uncached = await this.get(`v1/transit/toZips/${days}`, {
                fromZips,
                shipDate: estimatedShipDate,
                otdLevel,
            });
            _.toPairs(uncached).forEach(([fromZip, toZips]) => this.fromZipGroundToZipsCache.set(cacheKey(fromZip), toZips));
            if (verbose) {
                commons_logs_1.log.info({
                    cached: _.keys(cached),
                    uncached: fromZips,
                    days,
                    estimatedShipDate,
                }, "groundToZips cache status by fromZips");
            }
            return _.fromPairs(_.union(_.toPairs(cached), _.toPairs(uncached)));
        }
    }
    async getGroundToZipsByFromZipsForPrediction(predictionId, fromZip3s, days = 2, estimatedShipDate, otdLevel = business_types_1.OtdLevels.High, verbose = false) {
        const cacheKey = (zip) => [predictionId, zip, days, otdLevel, estimatedShipDate].filter(Boolean).join("-");
        const cached = _.fromPairs(fromZip3s
            .map((fromZip) => [fromZip, this.fromZipGroundToZipsCacheForPrediction.get(cacheKey(fromZip))])
            .filter((pair) => pair[1]));
        const fromZips = _.difference(fromZip3s, _.keys(cached));
        if (_.isEmpty(fromZips)) {
            if (verbose) {
                commons_logs_1.log.info({ fromZips: _.keys(cached), days, estimatedShipDate }, "Retrieved groundToZips from cache");
            }
            return cached;
        }
        else {
            const uncached = await this.get(`v2/transit/toZips/${days}`, {
                fromZips,
                shipDate: estimatedShipDate,
                otdLevel,
                predictionId,
            });
            _.toPairs(uncached.groundToZipsByFromZips).forEach(([fromZip, toZips]) => this.fromZipGroundToZipsCacheForPrediction.set(cacheKey(fromZip), toZips));
            if (verbose) {
                commons_logs_1.log.info({
                    cached: _.keys(cached),
                    uncached: fromZips,
                    days,
                    estimatedShipDate,
                    predictionId,
                }, "groundToZips cache status by fromZips");
            }
            return _.fromPairs(_.union(_.toPairs(cached), _.toPairs(uncached.groundToZipsByFromZips)));
        }
    }
    async getGroundToZipsByShipMethods(fromZip, days, estimatedShipDate, regionalShipMethods, zipDigits = 3, otdLevel = business_types_1.OtdLevels.High, includeDasZips = false, hubIds) {
        return this.getCached(`v1/transit/ship-methods/${days}`, {
            fromZip,
            shipDate: estimatedShipDate,
            regionalShipMethods,
            zipDigits,
            otdLevel,
            includeDasZips,
            hubIds,
        });
    }
    async getGroundToZipsByShipMethodsForPrediction(fromZip, days, estimatedShipDate, regionalShipMethods, zipDigits = 3, otdLevel = business_types_1.OtdLevels.High, includeDasZips = false, predictionId, hubIds) {
        return this.getCached(`v2/transit/ship-methods/${days}`, {
            fromZip,
            shipDate: estimatedShipDate,
            regionalShipMethods,
            zipDigits,
            otdLevel,
            includeDasZips,
            predictionId,
            hubIds,
        });
    }
    async getGroundShipMethodToZipsByDeliveryDate(request) {
        const { fromZip, maxDeliveryDays, shipDate, otdLevel, regionalShipMethods, zipDigits, includeDasZips, predictionId, hubIds, } = request;
        return this.getCached(`v1/transit/calendar-dates/${maxDeliveryDays}`, {
            fromZip,
            shipDate,
            regionalShipMethods,
            zipDigits,
            otdLevel,
            includeDasZips,
            predictionId,
            hubIds,
        });
    }
    async getFastTagDaysLeft(dsku) {
        return this.get(`v1/fast-tags/${dsku}`);
    }
    async getTransitTimeDays(fromZip, destZip, dates, includedShippingMethods, otdLevel = business_types_1.OtdLevels.High, includedHubIds) {
        return this.getCached("v2/delivery-days", {
            fromZip,
            destZip,
            includedShippingMethods,
            dates,
            otdLevel,
            includedHubIds,
        });
    }
    async getAggregateDemand(zipDigits = 3) {
        let aggregateDemand;
        try {
            aggregateDemand = await this.getCached(`v1/demand`, { zipDigits }, DAY_MS);
        }
        catch (error) {
            commons_logs_1.log.error({ fn: "AvailabilityClient#getAggregateDemand", error }, "Error, using local copy");
        }
        if (aggregateDemand) {
            return aggregateDemand;
        }
        return zipDigits === 3 ? Availability_1.AGGREGATE_DEMAND_BY_ZIP3 : require("./AggregateDemandByZip5.json");
    }
    async getTotalDemandQty() {
        return (await this.getAggregateDemand()).totalQty;
    }
    async getDemandByZips(zips) {
        if (_.isEmpty(zips)) {
            return {};
        }
        const zipDigits = zips[0].length;
        const demandByZips = (await this.getAggregateDemand(zipDigits)).demand;
        return _.fromPairs(zips.map((zip) => [zip, demandByZips[zip]]).filter((pair) => pair[1]));
    }
    async getAllZips(zipDigits = 5, includeDasZips = false) {
        return this.getCached("v1/zips", { zipDigits, includeDasZips });
    }
    async expandZips(zips, zipDigits = 5, includeDasZips = false) {
        return this.post("v1/zips", zips, {
            zipDigits,
            includeDasZips,
        });
    }
    async getDemandBySkuAndGeo(sellerId, dskus, zipDigits = 3) {
        const dskuChunks = _.chunk(dskus, AVAILABILITY_CLIENT_BATCH_LIMIT);
        const demandBySellerIdOrSkuAndGeo = _.merge({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.get("v1/demand", { sellerId, dskus: dskuChunk, zipDigits })))));
        return _.fromPairs(dskus
            .map((dsku) => [dsku, demandBySellerIdOrSkuAndGeo[dsku]])
            .map((pair) => (pair[1] ? pair : [pair[0], demandBySellerIdOrSkuAndGeo[sellerId]])));
    }
    async getDeliveryDisruption(deliveryDisruptionId) {
        return this.get(`v1/delivery-disruption/${deliveryDisruptionId}`);
    }
    async getDeliveryDisruptions() {
        return this.get("v1/delivery-disruption");
    }
    async getDeliveryDisruptionIdsByCorridor(shipMethod, fromZip, toZip, shippedAt, deliverBy) {
        return this.get("v1/delivery-disruption-ids", {
            shipMethod,
            fromZip,
            toZip,
            shippedAt,
            deliverBy,
        });
    }
    async getWarehouseZipOverrides() {
        return this.get(`/v1/transit-overrides`);
    }
    async getActivePredictions() {
        return this.get("v1/active-predictions");
    }
    async getActivePredictionsByOtdLevel() {
        return this.get("v2/active-predictions");
    }
    async getShippingMethodZone(shippingMethod, fromZip, toZip) {
        const url = `/v1/zone/${shippingMethod}/${toZip}/${fromZip}`;
        return this.getCached(url, {}, 3600000);
    }
    async getAllShippingMethodZones(shippingMethod) {
        const url = `/v1/zone/${shippingMethod}`;
        return this.getCached(url, {}, 3600000);
    }
    async updateCarrierZone(shippingMethod, zoneChart) {
        const url = `/v1/zone/${shippingMethod}`;
        return this.put(url, zoneChart);
    }
    async getStateFromZip(zipcode) {
        return this.get(`/v1/zip/${zipcode}`);
    }
    async getDistanceBetweenTwoZips(fromZip, toZip) {
        return this.get(`/v1/zip/distance/${fromZip}/${toZip}`);
    }
}
exports.AvailabilityClient = AvailabilityClient;
//# sourceMappingURL=AvailabilityClient.js.map